// Static learn articles — each becomes a public SEO page at /learn/:slug
// Add new articles here; they auto-appear in the index and get their own URL.

export const ARTICLES = [
  {
    slug: "how-to-automate-zerodha-trading",
    title: "How to Automate Zerodha Trading in 2025 (Without Coding)",
    description: "Step-by-step guide to setting up automated trading on Zerodha using AlgoForge — strategy builder, paper testing, and live execution.",
    category: "getting-started",
    readTime: 6,
    keywords: "automate zerodha trading, zerodha algo trading, automated trading india zerodha",
    body: `## Can You Really Automate Zerodha Trading Without Coding?

Yes — and it's more accessible than most traders think. Zerodha's Kite Connect API is powerful but requires Python or JavaScript knowledge. AlgoForge sits on top of that API and gives you a visual interface to build, backtest, and automate strategies without writing a single line of code.

Here's exactly how it works.

## Step 1: Connect Your Zerodha Account

Go to **Brokers** in AlgoForge and click **Connect Zerodha**. You'll be redirected to Kite's OAuth flow — log in, approve the connection, and you're done. AlgoForge never sees your password; it only receives a time-limited API token.

> Your credentials are encrypted with Fernet AES-128 before being stored. AlgoForge cannot access your funds or place orders without your explicit strategy configuration.

## Step 2: Build Your Strategy (No Coding)

The **Strategy Builder** gives you a visual rule editor:
- Choose your **entry signal** (VWAP crossover, EMA crossover, RSI level, price breakout)
- Set your **position size** (fixed quantity or % of capital)
- Add your **exit rules** (target %, stop-loss %, time-based)
- Enable **bracket orders** for automatic SL + target placement

Example: "Buy NIFTY50 when 5-min candle closes above VWAP with RSI > 55. Exit at +0.8% target or -0.4% stop-loss."

## Step 3: Backtest Before Going Live

Run your strategy against historical NSE data before risking real money. The backtest shows you:
- Total returns, max drawdown, Sharpe ratio
- Win rate and average profit per trade
- Equity curve and worst drawdown periods

**Rule of thumb**: Only go live with a strategy that has 100+ backtest trades and a Sharpe ratio above 1.0.

## Step 4: Paper Trade First

AlgoForge gives every user ₹10,00,000 in virtual capital. Run your strategy in **paper mode** for at least 2 weeks before switching to live. Paper trading uses real market prices — only the money is virtual.

## Step 5: Go Live

Once you're confident, flip the switch to **Live** mode. AlgoForge routes orders through your Zerodha account via Kite Connect. You can set daily loss caps and a kill switch to auto-pause trading if losses exceed your limit.

## Key Limitations to Know

- Zerodha Kite Connect requires a paid API subscription (₹2,000/month or free for 500+ trades/month)
- Automated strategies work best for MIS (intraday) — overnight Novernight positions require additional setup
- Zerodha's API has a rate limit of 3 orders/second — high-frequency strategies won't work

## SEBI Compliance Note

AlgoForge is a **technology tool**, not a SEBI-registered investment advisor. The platform helps you implement *your own* strategies — it does not provide buy/sell recommendations. This is the same model used by Tradetron, Streak, and other algo platforms in India.

Ready to try it? [Start with free paper trading →](/register)`,
  },
  {
    slug: "what-is-iv-rank-options-india",
    title: "What is IV Rank in Options Trading? (India Guide)",
    description: "IV Rank explained for Indian options traders — how to calculate it, what readings mean for Nifty/BankNifty, and how to use it to pick strategies.",
    category: "options",
    readTime: 5,
    keywords: "iv rank options india, implied volatility rank nifty, options iv rank calculator, high iv options strategy india",
    body: `## What is IV Rank?

IV Rank (Implied Volatility Rank) tells you where the current Implied Volatility of a stock or index sits relative to its own history over the past 52 weeks.

**Formula:**
\`\`\`
IV Rank = (Current IV - 52W Low IV) / (52W High IV - 52W Low IV) × 100
\`\`\`

**Example for Nifty:**
- 52-week IV Low: 10%
- 52-week IV High: 28%
- Current IV: 18%
- IV Rank = (18 - 10) / (28 - 10) × 100 = **44**

An IV Rank of 44 means current volatility is in the middle of its annual range — neither cheap nor expensive.

## Why IV Rank Matters More Than IV Itself

Raw IV numbers are meaningless without context. Nifty at 14% IV sounds low — but if it spent the last year between 12-16%, that's actually expensive.

IV Rank normalizes this. The same number (say, 70) means the same thing across all stocks and indices: IV is in the top 30% of its annual range.

## How to Read IV Rank for Indian Options

| IV Rank | What It Means | Strategy Bias |
|---------|--------------|---------------|
| 0–30 | IV is LOW — options are cheap | Buy premium (straddles, strangles, long calls/puts) |
| 30–70 | IV is NEUTRAL | Iron Condors, covered calls, spreads |
| 70–100 | IV is HIGH — options are expensive | Sell premium (short straddles, iron condors, cash-secured puts) |

**The core principle**: Buy options when IV is low (cheap), sell options when IV is high (expensive). Selling high IV means you collect more premium, and if IV reverts to its mean (which it almost always does), you profit from the "IV crush."

## IV Rank Around Indian Market Events

IV spikes predictably around:
- **RBI MPC meetings** (quarterly) — IV rises 2-3 days before, crashes after the announcement
- **Union Budget** — biggest single-day IV spike of the year
- **Quarterly results** — individual stock IV spikes 1-2 weeks before
- **Global events** — US Fed meetings, geopolitical events

Smart Indian options traders *sell premium before these events* (high IV) and *buy premium after* (IV crash resets it low).

## IV Rank vs IV Percentile — Which to Use?

**IV Rank**: Compares current IV to 52W High and Low. Can be misleading if there was one extreme spike.

**IV Percentile**: Counts how many days in the past year had IV below the current level. More robust — use this when a stock had an unusual spike.

AlgoForge shows both. When they diverge significantly, pay attention to IV Percentile.

## Checking IV Rank in AlgoForge

Go to **Options → Market Sentiment** tab. You'll see real-time IV Rank and IV Percentile for Nifty 50, BankNifty, and individual F&O stocks.

The gauge turns green (low IV → consider buying) or red (high IV → consider selling) with automatic strategy suggestions for the current environment.

[Check live IV Rank for Nifty →](/options)`,
  },
  {
    slug: "bracket-order-vs-cover-order-india",
    title: "Bracket Order vs Cover Order — Which One Should You Use?",
    description: "Clear comparison of bracket orders and cover orders for Indian traders on Zerodha, Dhan, and Upstox. When to use each, and how to automate them.",
    category: "trading",
    readTime: 5,
    keywords: "bracket order vs cover order, bracket order zerodha, cover order india, automated stop loss india",
    body: `## The One-Line Difference

**Cover Order**: Entry + mandatory stop-loss. Two legs.

**Bracket Order**: Entry + stop-loss + target. Three legs. The system closes your position automatically when either the SL or target is hit.

For most intraday traders, **bracket orders are strictly better** — they enforce discipline automatically and remove the temptation to "let it run a bit more" past your target.

## Cover Order — How It Works

When you place a Cover Order, you place two orders simultaneously:
1. Your entry order (market or limit)
2. A stop-loss order (mandatory — Zerodha won't accept a Cover Order without it)

The stop-loss is placed as an SL-M (stop-loss market) order. If your entry price is ₹100 and you set SL at ₹97, your position closes automatically if the price drops to ₹97.

**Use cover orders when**: You don't have a fixed target and want to let the position run with a trailing stop.

## Bracket Order — How It Works

Bracket orders place three orders simultaneously:
1. Entry order
2. Stop-loss order (SL-M)
3. Target order (limit sell/buy)

When either the SL or target triggers, the system automatically cancels the remaining order and closes your position. You can also set a **trailing stop** that moves the SL up as the price moves in your favor.

**Use bracket orders when**: You have a defined risk-reward setup (e.g., 1:2 RR) and want full automation.

## Margin Benefit — The Real Reason Traders Use These

Both Cover Orders and Bracket Orders give you **higher leverage** than regular MIS orders because the SL is pre-placed — the broker's risk is capped.

Typical leverage comparison (NSE F&O, varies by broker):

| Order Type | Typical Margin |
|------------|----------------|
| Normal MIS | 1x–5x |
| Cover Order | Up to 10x |
| Bracket Order | Up to 10x |

> Always check your broker's margin calculator before placing leveraged orders. Margins change frequently based on SEBI/exchange circulars.

## Automated Bracket Orders in AlgoForge

AlgoForge's **bracket order system** adds a layer on top of your broker's native bracket orders:

- Set entry, SL, and target as **percentages** rather than absolute prices
- Enable **trailing stop** (SL moves up by X% as price moves in your favor)
- The AlgoForge bracket monitor checks prices every 2 seconds and triggers closes automatically

This works even if your broker's bracket order system has limitations (Zerodha's native bracket orders have some conditions around market hours).

## Which Brokers Support Bracket Orders?

| Broker | Bracket Orders | Cover Orders |
|--------|---------------|--------------|
| Zerodha | Yes (MIS only) | Yes |
| Dhan | Yes | Yes |
| Upstox | Limited | Yes |

AlgoForge handles bracket order logic at the platform level for brokers with limited native support.

[Set up your first bracket order →](/register)`,
  },
  {
    slug: "what-is-paper-trading-india",
    title: "What is Paper Trading? (And Why Every Indian Trader Should Use It)",
    description: "Paper trading explained — how it works, why it matters for Indian retail traders, and how to start with ₹10 lakh virtual capital on AlgoForge.",
    category: "getting-started",
    readTime: 4,
    keywords: "paper trading india, virtual trading india free, paper trading zerodha alternative, practice trading india",
    body: `## Paper Trading — The Closest Thing to Risk-Free Trading

Paper trading (also called virtual trading or simulated trading) means placing trades with fake money but real market prices. Every order is simulated against live NSE/BSE price feeds — the only difference is that no real money changes hands.

It's called "paper trading" because traders used to literally write their orders on paper before the internet.

## Why Indian Traders Underestimate It

Most retail traders skip paper trading and go straight to live markets. Then they lose money, blame "bad luck," and quit. The real reason is usually one of these:

- **Strategy not tested**: What looks good on a chart doesn't always work in live conditions
- **Execution slippage**: Orders fill at worse prices than expected, especially in illiquid stocks
- **Emotional execution**: Fear and greed cause traders to break their own rules
- **Position sizing errors**: Too large on losses, too small on winners

Paper trading exposes all of these *before* real money is at risk.

## What Paper Trading Can and Cannot Test

**Can test:**
- Whether your entry and exit rules are clearly defined
- Typical win rate and profit factor over 20-50 trades
- How you emotionally react to seeing a position go against you
- Whether your strategy works in different market conditions (trending vs. sideways)

**Cannot fully replicate:**
- The emotional weight of real money losses
- Slippage in illiquid stocks (paper fills assume perfect execution)
- Impact of your own orders in less liquid instruments

## How AlgoForge Paper Trading Works

Every AlgoForge account starts with **₹10,00,000 in virtual capital**. Paper trades execute against real NSE/BSE prices from your connected broker's feed.

Key features:
- **Full P&L tracking**: Running P&L, realized gains, position-wise breakdown
- **Bracket orders**: Test automated SL + target workflows
- **Signal integration**: Run strategy signals directly into paper orders
- **Auto square-off**: MIS positions close at 15:05 IST for cash equity and 15:15 IST for NSE F&O (MIS simulation)
- **Audit trail**: Complete order history, just like live trading

## How Long to Paper Trade Before Going Live?

Minimum: **4 weeks, 50+ trades**.

The goal isn't to paper trade forever — it's to reach statistical confidence. 50 trades is the minimum to see if your win rate is real or luck. 4 weeks ensures you've traded across different market conditions.

A practical milestone: If your paper trading strategy shows a Sharpe ratio above 1.0 and max drawdown below 15% over 50+ trades, you're ready to go live with a small capital allocation.

[Start paper trading with ₹10 lakh virtual capital →](/register)`,
  },
  {
    slug: "what-is-put-call-ratio-india",
    title: "Put-Call Ratio (PCR) Explained for Indian Options Traders",
    description: "How to read and use Put-Call Ratio for Nifty and BankNifty options trading. PCR interpretation, levels, and contrarian signals.",
    category: "options",
    readTime: 5,
    keywords: "put call ratio nifty, pcr options india, put call ratio interpretation, nifty pcr today",
    body: `## What is the Put-Call Ratio?

The Put-Call Ratio (PCR) measures market sentiment by comparing the open interest (or volume) in put options to call options.

**Formula:**
\`\`\`
PCR = Total Put Open Interest / Total Call Open Interest
\`\`\`

A PCR of 1.0 means equal put and call OI — perfectly neutral. Above 1.0 = more puts than calls = bearish positioning. Below 1.0 = more calls than puts = bullish positioning.

## How to Read PCR for Nifty

This is where most traders get confused. PCR is a **contrarian indicator**.

| PCR Level | What It Signals | Contrarian Reading |
|-----------|----------------|-------------------|
| Below 0.7 | Extreme call buying — market very bullish | **Caution**: Too much greed, possible reversal |
| 0.7–1.0 | Moderate bullishness | Mild bullish bias |
| 1.0–1.3 | Neutral to mild bearish | Market in balance |
| Above 1.3 | Extreme put buying — market very bearish | **Opportunity**: Too much fear, possible bounce |

When everyone is buying puts (high PCR), the market is pricing in fear. Contrarian traders *sell those puts* or go long, expecting the fear to be overdone. This has worked historically in Nifty — extreme PCR readings tend to mean-revert.

## OI-Based PCR vs Volume PCR — Which to Watch?

**OI-based PCR** (Open Interest): Tells you total outstanding positions. Better for gauging *structural* market bias. This is what institutional traders track.

**Volume-based PCR**: Tells you *today's* trading activity. More volatile, less reliable for trend reading.

For Nifty and BankNifty, always use **OI-based PCR** for strategic decisions.

## PCR Across Different Expiries

Don't just look at the overall PCR — break it down:

- **Weekly expiry PCR**: Short-term sentiment, very reactive to news
- **Monthly expiry PCR**: Medium-term positioning, more reliable signal
- **Far-month PCR**: Hedging by institutions, less useful for retail traders

AlgoForge shows PCR separately for each expiry so you can see if institutional money is hedging (far month) or speculating (near month).

## Using PCR With Other Indicators

PCR alone is not a trading signal — it's a filter.

**High PCR (>1.3) + RSI oversold + Price at support** → High-probability long setup

**Low PCR (<0.7) + RSI overbought + Price at resistance** → High-probability short setup

The PCR confirms the broader picture. Never trade PCR alone.

## Live PCR on AlgoForge

The **Options → Market Sentiment** tab shows real-time PCR for Nifty 50 and BankNifty across all expiries. The dashboard includes a PCR trend chart so you can see how it's moved over the last 5 days.

[Check live Nifty PCR →](/options)`,
  },
  {
    slug: "algo-trading-india-without-coding",
    title: "Algo Trading in India Without Coding — Complete 2025 Guide",
    description: "How to start algorithmic trading in India without programming. Tools, platforms, SEBI rules, and step-by-step setup for retail traders.",
    category: "getting-started",
    readTime: 7,
    keywords: "algo trading india without coding, algorithmic trading india beginner, automated trading india no code, algo trading zerodha dhan upstox",
    body: `## Is Algo Trading in India Only for Programmers?

Not anymore. Until 2019, algo trading in India genuinely required programming skills — you needed to write Python scripts for Zerodha's Kite API or use expensive institutional platforms.

The landscape changed dramatically with the rise of no-code algo platforms. Today, a retail trader with zero coding experience can automate an options strategy in an afternoon.

## What Algo Trading Actually Means for Retail Traders

"Algo trading" covers a spectrum:

**Level 1 — Automated Execution**: Your strategy rules are fixed, but a platform executes them automatically when conditions are met. No human clicks required.

**Level 2 — Strategy Backtesting**: Test your strategy on historical data before risking real money. Find out if your edge is real or just luck.

**Level 3 — Quantitative Strategy Development**: Statistical analysis, machine learning, factor models. This is where coding helps.

Most retail traders need Levels 1 and 2. You don't need Level 3.

## SEBI Rules on Algo Trading (2025)

SEBI's January 2025 circular changed the rules for retail algo trading:

- **Broker approval required**: All algos must be registered with your broker before going live
- **Whitelisted platforms**: Brokers can whitelist technology platforms (like AlgoForge) for algo order routing
- **Audit trail**: All algo orders must be tagged and traceable
- **No third-party signals**: Your algo must execute *your own* strategies, not recommendations from another party

The practical impact: You need to use a broker-whitelisted platform, and your strategies need to be registered through that platform. AlgoForge handles this registration process as part of the broker connection setup.

## No-Code Platforms Comparison

| Platform | Strategy Builder | Backtesting | Brokers Supported | Pricing |
|----------|-----------------|-------------|-------------------|---------|
| AlgoForge | Visual + AI | Yes | Zerodha, Dhan, Upstox | Free + ₹999/mo Pro |
| Tradetron | Flow-based | Limited | Most brokers | ₹1,999/mo+ |
| Streak (Zerodha) | Condition-based | Yes | Zerodha only | ₹999/mo |
| Sensibull | Options only | No | Multiple | ₹800/mo |

AlgoForge differentiates on AI Copilot (explain/optimize strategies in plain language), built-in risk management (kill switch, drawdown limits), and multi-broker support.

## Step-by-Step: Your First Automated Strategy

### 1. Choose a Simple Strategy to Start

Don't start with complex multi-leg options or ML models. Start with something you understand intuitively:

**Beginner-friendly strategies:**
- VWAP reversion: Buy when price is >1% below VWAP, exit at VWAP
- Opening range breakout: Buy breakout of first 15-min candle high after 9:30am
- EMA crossover: Buy when 9-EMA crosses above 21-EMA on 5-min chart

### 2. Build It in the Strategy Builder

Use plain-language conditions: "Entry when [indicator] [condition] [value]". Set your SL as a percentage (e.g., 0.5%) and target (e.g., 1.0%). Enable bracket orders for automatic execution.

### 3. Backtest on 6+ Months of Data

Run the backtest. Look for:
- **Win rate**: Aim for >45% (you don't need to win every trade)
- **Profit factor**: Total profit / Total loss > 1.5
- **Max drawdown**: Should be < 20% of starting capital
- **Sharpe ratio**: > 1.0 is acceptable, > 1.5 is good

If the backtest fails these benchmarks, tweak the strategy and re-test. Don't go live with an untested strategy.

### 4. Paper Trade for 3-4 Weeks

Run the strategy on paper money with real market prices. Note any discrepancies between backtest expectations and actual paper results. Common causes: slippage, different market conditions, strategy parameter overfitting.

### 5. Go Live with Small Capital

Start with ₹50,000–₹1,00,000. At this size, even a bad month costs you tuition money, not life savings. Increase capital allocation only after 3+ months of consistent live results.

## The Discipline Edge — Why Automation Actually Works

Most retail traders don't lose because of bad strategies. They lose because they don't follow their strategies.

- They move stop-losses hoping the trade recovers
- They take profits too early out of fear
- They overtrade on bad days trying to recover losses
- They skip good setups because they "feel wrong"

Automation removes these failure modes. The system executes the strategy exactly as designed, every time, without emotion. That's the real edge of algo trading for retail traders.

[Build your first automated strategy →](/register)`,
  },
  {
    slug: "nifty-options-expiry-strategy",
    title: "Nifty Weekly Options Expiry Strategy — What Actually Works",
    description: "Proven approaches for trading Nifty 50 weekly options expiry on Thursdays. IV crush timing, max pain theory, and low-risk expiry plays.",
    category: "options",
    readTime: 6,
    keywords: "nifty weekly expiry strategy, nifty options thursday expiry, nifty expiry day trading, options expiry strategy india",
    body: `## Why Expiry Day is Different

Every Thursday (Nifty 50) and Wednesday (BankNifty), options expire. The dynamics on expiry day are fundamentally different from regular trading:

- **Time decay accelerates**: Theta (time decay) is at its maximum — options lose value fastest in the final hours
- **Gamma spikes**: OTM options have extreme gamma — small price moves cause large % changes in option premiums
- **IV crush**: After the expiry settlement, volatility collapses (though it rebuilds for the next week)
- **Market makers adjust**: Large players actively manage their positions, creating predictable price behavior near key strikes

## The Max Pain Theory — Does It Work for Nifty?

Max Pain is the strike price at which the total loss for all option buyers is maximized at expiry. Option writers (sellers) profit most at this price.

**The theory**: Market makers and institutions with large short option positions have incentives to pin the index near Max Pain at expiry.

**Does it work in practice?** Partially. Studies on Nifty show:
- Nifty settles within ±1% of Max Pain strike in approximately 65-70% of weekly expiries
- The effect strengthens in the final 2 hours of trading (1:00pm–3:30pm IST)
- It fails badly during high-volatility weeks (RBI events, global shocks)

Max Pain is best used as a *reference point* for where the market may gravitate, not as a standalone signal.

## Three Expiry Strategies (With Risk Profiles)

### Strategy 1: Short Straddle at ATM (High Risk, High Reward)

**Setup**: Sell ATM Call + Sell ATM Put, 1-2 hours before expiry close
**Logic**: Maximum theta decay in final hours; if market stays near current level, both options expire worthless
**Risk**: A sudden large move wipes out premium collected
**Best when**: Low volatility day, no major news expected, PCR between 0.8-1.2

### Strategy 2: Iron Condor Near Max Pain (Moderate Risk)

**Setup**: Sell OTM Call + Buy further OTM Call + Sell OTM Put + Buy further OTM Put, centered around Max Pain
**Logic**: Collect premium from both sides, max pain acts as a gravitational center
**Risk**: Limited (defined by the spread width)
**Best when**: PCR between 1.0-1.5, IV Rank above 50, market in consolidation

### Strategy 3: OTM Directional Buy (Lottery Ticket)

**Setup**: Buy deep OTM Call or Put with 2-3 hours to expiry, ₹1-3 premium
**Logic**: If a large move happens (RBI news, global market shock), the option multiplies 10-20x
**Risk**: Near total loss if market doesn't move decisively
**Best when**: Known catalyst expected (budget speech, Fed announcement on expiry day)

## Timing Your Expiry Trades

| Time | What Happens | What to Do |
|------|-------------|-----------|
| 9:15–10:30am | Market finds direction, high volatility | Avoid new positions |
| 10:30am–1:00pm | Consolidation phase, theta starts accelerating | Enter spread positions if setup is right |
| 1:00pm–2:30pm | Max Pain magnetism strongest | Short straddle/condor entries |
| 2:30pm–3:25pm | Final positioning, rapid time decay | Let positions run or add theta plays |
| 3:25pm–3:30pm | Settlement | Close all positions — don't hold to final minute |

## Using AlgoForge on Expiry Day

The **Options → Market Sentiment** tab shows live Max Pain for the current weekly expiry, updated every 15 minutes as OI data refreshes.

Use the payoff diagram to visualize your expiry strategy before entering. Set price alerts at your SL levels so you're notified immediately if the trade goes against you.

[View live Max Pain and set up expiry alerts →](/register)`,
  },
  {
    slug: "options-chain-analysis-india",
    title: "How to Read an Options Chain — Complete Guide for Indian Traders",
    description: "Master options chain analysis for Nifty and BankNifty. Understanding strikes, OI, Greeks, and what the chain tells you about market direction.",
    category: "options",
    readTime: 7,
    keywords: "options chain analysis india, how to read options chain nifty, open interest analysis options, options greeks explained india",
    body: `## What is an Options Chain?

An options chain is a table showing all available option contracts for a particular underlying (like Nifty 50) across all strike prices and expiry dates. It shows real-time prices, open interest, volume, and Greeks for both calls and puts.

Reading an options chain correctly tells you what the market is *positioned for* — not just where it is now.

## Anatomy of the Nifty Options Chain

The chain has calls on the left, puts on the right, and strike prices in the middle. Here's what each column means:

**OI (Open Interest)**: Total outstanding contracts at that strike. High OI = strong support/resistance level. Call OI at a strike = writing (sellers), meaning resistance. Put OI at a strike = support.

**Change in OI**: How much OI changed today. New positions being added = conviction.

**Volume**: Contracts traded today. High volume + rising OI = trend. High volume + falling OI = position unwinding.

**IV (Implied Volatility)**: Expected annualized volatility priced into that specific option. The "IV smile" or "skew" tells you which direction the market fears more.

**LTP**: Last traded price. Premium you'd pay to buy or receive to sell.

## The Greeks — What They Mean Practically

**Delta (Δ)**: How much the option price changes for every ₹1 move in Nifty.
- ATM call: ~0.50 delta (moves ₹0.50 for every ₹1 Nifty move)
- Deep ITM call: ~0.90 delta (almost tracks the index)
- Deep OTM call: ~0.05 delta (barely moves)

**Theta (Θ)**: Daily time decay. A theta of -5 means you lose ₹5 per day you hold the option (if nothing else changes). Sellers collect theta; buyers pay it.

**Vega (V)**: Sensitivity to IV change. Long options benefit from rising IV; short options benefit from falling IV.

**Gamma (Γ)**: Rate of change of delta. High gamma = delta changes quickly (dangerous near expiry).

## Reading Support and Resistance from OI

This is the single most useful skill in options chain analysis:

**Call OI concentration = Resistance**: If 19,500 CE has 5 lakh contracts of OI, it means many sellers have sold that call. They'll defend 19,500 as resistance because their trade profits if Nifty stays below it.

**Put OI concentration = Support**: If 19,000 PE has 4 lakh contracts of OI, sellers of that put defend 19,000 as support.

Look for strikes with the highest OI — those are your key levels for the week.

## IV Skew — What Fear Looks Like

In a normal market, puts (downside protection) are more expensive than equivalent-distance calls. This is the "volatility skew" — the market pays a premium for downside hedges.

**Steep skew** (puts much more expensive than calls): Market fears a crash. Institutions are heavily hedging.

**Flat or reverse skew** (calls more expensive than puts): Market fears missing a rally upside. Unusual and often precedes corrections.

For Nifty, watch if the 5% OTM put IV is more than 5-6 points higher than the 5% OTM call IV. When this gap widens, fear is building.

## Using AlgoForge's Options Chain

The **Options → Chain** tab shows the full Nifty options chain with:
- Real-time OI, volume, and IV for every strike
- Color-coded OI concentrations (support/resistance heat map)
- ATM straddle portfolio Greeks (your position-level risk if you sold the straddle)
- IV skew chart showing the full volatility surface

The chain auto-highlights the Max Pain strike and the highest OI call/put strikes so you can immediately see the key levels.

[Explore the live options chain →](/register)`,
  },
  {
    slug: "how-to-set-price-alerts-stocks-india",
    title: "How to Set Price Alerts for Stocks and Options in India",
    description: "Set up automatic price alerts for NSE stocks, Nifty, and BankNifty options via Telegram and email. No manual monitoring needed.",
    category: "getting-started",
    readTime: 4,
    keywords: "price alert stocks india, nifty price alert telegram, stock alert india free, options price alert zerodha",
    body: `## Why Price Alerts Are Not Optional for Serious Traders

Most retail traders watch their screens all day — or worse, keep refreshing their broker app every few minutes. This is a massive productivity drain and leads to emotional decisions.

Price alerts solve this: you define the levels that matter, get notified instantly when they're hit, and spend the rest of your time doing something else.

## What to Set Alerts For

**Key price levels:**
- Support and resistance levels on your watchlist
- Previous day's high/low for intraday reference
- 52-week high/low for swing trades
- Pivot points (daily, weekly)

**Options-specific alerts:**
- Option premium at your target entry level (e.g., "Alert me when Nifty 19000 PE hits ₹50")
- Stop-loss level (e.g., "Alert me when my option drops below ₹20")
- IV Rank crossing above 70 (signal to consider selling premium)

**News-adjacent levels:**
- Before RBI policy dates, set alerts at key support/resistance
- Pre-budget: set alerts 1% above/below current level

## Setting Up Price Alerts in AlgoForge

Go to **Price Alerts** in the sidebar. Setting an alert takes 30 seconds:

1. **Symbol**: Type any NSE/BSE symbol or derivative (e.g., NIFTY50, RELIANCE, NIFTY2411219000PE)
2. **Condition**: "Price rises above" or "Price falls below"
3. **Target price**: The exact level you want to be notified at
4. **Notification**: Telegram (instant) or email (within minutes)

Alerts run 24/7 during market hours. When triggered, the alert auto-disarms (so you don't get spammed) but can be re-armed with one click.

## Telegram Alerts — The Best Delivery Method

For Indian traders, Telegram is the fastest and most reliable notification channel. Phone notifications arrive within 2-3 seconds of the alert condition being met.

Setup: Go to **Settings → Notifications**, enter your Telegram chat ID. To find your chat ID, message [@userinfobot](https://t.me/userinfobot) on Telegram.

Once set up, alerts look like:
\`\`\`
🔔 AlgoForge Alert
NIFTY50 crossed ABOVE ₹19,500
Current price: ₹19,503
Time: 10:32:15 IST
\`\`\`

## Smart Alert Strategies

**The bracket alert**: Set two alerts simultaneously — one above resistance, one below support. Whichever fires tells you the direction of the breakout.

**The entry trigger**: Set an alert at your planned entry price. When it fires, you have 30 seconds to decide if you still want the trade based on current context.

**The options entry**: Watch for when IV Rank crosses 70 (high IV). Set an alert on your watchlist symbol at current price — when it triggers, check the options chain and consider selling premium.

[Set your first price alert →](/register)`,
  },
  {
    slug: "discipline-trading-india-psychology",
    title: "Trading Discipline — The Real Edge That Most Indian Traders Ignore",
    description: "Why 90% of Indian retail traders lose money (it's not strategy), and how systematic automation solves the discipline problem permanently.",
    category: "trading",
    readTime: 5,
    keywords: "trading discipline india, trading psychology india, why retail traders lose money india, systematic trading india",
    body: `## The Uncomfortable Truth About Trading Losses

SEBI published data showing that 89% of individual equity F&O traders in India lost money between FY2022 and FY2024. The average loss was ₹1.1 lakh per trader.

The common explanation is "they had bad strategies." The real explanation is more uncomfortable: **most traders have fine strategies — they just don't follow them.**

## The Four Discipline Failures That Kill Accounts

**1. Moving stop-losses**
You set a stop-loss at ₹97 (entry ₹100). Price drops to ₹98. You move the SL to ₹95, "just to give it room." Price drops to ₹93. You move it again. Now you're down 7% instead of 3%.

This is not a strategy failure. It's a discipline failure.

**2. Revenge trading**
A bad morning trade loses ₹5,000. To "get it back," you take a larger trade in the afternoon. That loses ₹8,000. By 3pm, you've made one trade you planned and three you didn't.

**3. Skipping valid signals**
Your strategy triggers a buy signal. But it's a Monday after a bad Friday close and you "feel" the market will go down. You skip the signal. It would have been a winner.

**4. Holding losers, cutting winners**
Human psychology defaults to this: take profits quickly (fear of giving them back) and hold losers (hoping they recover). This is exactly backwards from what profitable trading requires.

## Why Rules Don't Fix Discipline Problems

You've probably written trading rules for yourself. "Never move a stop-loss." "No revenge trading." "Follow the system."

They don't work because willpower is finite. After a string of losses, in the heat of the moment, the rules feel abstract and the loss feels concrete. Your brain rationalizes rule-breaking.

The only permanent fix is **removing yourself from the decision loop**.

## Automation as a Discipline Tool

When a system executes your trades automatically:
- Stop-losses execute at the exact price you set — no negotiation
- Signals execute without your emotional approval
- Daily loss limits halt trading automatically — no "just one more trade"
- You review results, not make decisions

This is why professional traders use systematic strategies. Not because computers are smarter — because they don't have emotions.

## The Risk Management Layer

AlgoForge has several built-in discipline tools:

**Kill Switch**: Set a daily loss limit (e.g., ₹5,000). If paper or live P&L drops below that, all trading halts automatically. You can't override it in the moment — you'd have to manually disable it first, which creates a pause.

**Max Drawdown**: Set a maximum portfolio drawdown. If your account value drops more than X% from its peak, trading stops.

**Position Limits**: Cap the maximum number of open positions. Prevents you from pyramiding into a losing trade.

These aren't constraints on your freedom — they're constraints on your worst moments.

## Starting Small: The Paper Trading Bridge

If you're not ready for full automation on live capital:

1. Write your strategy rules completely (entry, exit, position size, max daily loss)
2. Paper trade the strategy for 4 weeks *without deviation* — treat it as a discipline test
3. Review each deviation: why did you break the rule? What was the outcome?
4. After 4 weeks of rule-following on paper, go live with a small amount

The discipline muscle gets trained on virtual money. By the time real money is involved, rule-following is a habit.

[Start building disciplined trading habits with paper trading →](/register)`,
  },
];

export const CATEGORIES = {
  "getting-started": "Getting Started",
  "options": "Options & Derivatives",
  "trading": "Trading & Strategy",
};
